import { useBeneficiarySubmissionContext } from '@/components/blocks/BeneficiarySubmissionContext';
import { CartSetupRender } from '@/components/blocks/CartRender';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import { Img } from '@/components/blocks/MaterialImage';
import { OneClick } from '@/components/blocks/OneClick';
import { HeaderMiniCartDropMenuItemSkeleton } from '@/components/content/Header/parts/HeaderMiniCartDropMenuItemSkeleton';
import { HeaderMiniCartDropMenuItemV2 } from '@/components/content/Header/parts/HeaderMiniCartDropMenuItemV2';
import { HeaderMiniCartTourStart } from '@/components/content/Header/parts/HeaderMiniCartTourStart';
import { headerMiniCartItemSX } from '@/components/content/Header/styles/miniCart/item';
import { useCart } from '@/data/Content/Cart';
import { useProductDetailsTour } from '@/data/Content/ProductDetailsTour';
import { useLocalization } from '@/data/Localization';
import { formatPrice } from '@/utils/formatPrice';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, ClickAwayListener, Divider, Modal, Skeleton, Stack, Typography } from '@mui/material';
import React, { FC } from 'react';

export const HeaderMiniCartDropMenuV2: FC<{
	onClose: () => void;
	onDeleteCart: () => Promise<void>;
	open: boolean;
}> = ({ open, onClose, onDeleteCart }) => {
	const CartLabels = useLocalization('MiniCart');
	const { data: order, orderItems } = useCart();
	const beneficiarySubmissionContext = useBeneficiarySubmissionContext();
	const { onFinishProductDetailsTour, onAddToCartProductDetailsTour } = useProductDetailsTour();

	const handleClose = () => {
		onFinishProductDetailsTour();
		onClose();
	};

	return (
		<CartSetupRender order={order} onDeleteCart={onDeleteCart} onClose={handleClose}>
			{({ productsGroup, grandTotal, loading, products, onEmptyCart, removeAll }) => (
				<Modal
					open={open}
					onClose={handleClose}
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<ClickAwayListener onClickAway={handleClose}>
						<HeaderMiniCartTourStart
							sx={{
								width: '580px',
								backgroundColor: '#FFFFFF',
								borderRadius: '6px 6px 6px 6px',
								position: 'relative',
							}}
							loading={(productsGroup?.length ?? 0) === 0}
							onAddToCartProductDetailsTour={onAddToCartProductDetailsTour}
							onFinishProductDetailsTour={onFinishProductDetailsTour}
						>
							<ClearIcon
								htmlColor="#44484B"
								sx={{
									position: 'absolute',
									fontSize: '24px',
									cursor: 'pointer',
									marginLeft: 'auto',
									top: 14,
									right: 14,
								}}
								onClick={onClose}
							/>
							<Stack
								sx={(theme) => ({
									paddingX: theme.dimensions.dpSpacing.spacing7,
									paddingBottom: theme.dimensions.dpSpacing.spacing7,
									marginTop: '37px',
								})}
							>
								{orderItems?.length > 0 ? (
									<Stack>
										<Stack
											direction="column"
											sx={(theme) => ({
												marginBottom: theme.dimensions.dpSpacing.spacing3,
											})}
										>
											<Stack
												direction="row"
												sx={{
													alignItems: 'center',
												}}
											>
												<Typography
													sx={{
														fontSize: '26px',
														color: '#414340',
														fontWeight: 700,
														display: 'flex',
														alignItems: 'center',
													}}
												>
													<Img
														src="/images/check-circle.png"
														alt="check-circle"
														sx={{ height: '24px', width: '24px', marginRight: '6px' }}
													/>
													{'Agregaste a tu pedido'}
												</Typography>
												<OneClick
													wrapper="button-base"
													sx={(theme) => ({
														fontSize: '16px',
														marginLeft: theme.dimensions.dpSpacing.spacing4,
														color: '#2C2C2C',
														textDecoration: 'underline',
														width: 'fit-content',
														alignSelf: 'center',
													})}
													disabled={loading}
													onClick={onEmptyCart}
												>
													{'Eliminar todo'}
												</OneClick>
											</Stack>
											<Typography
												sx={(theme) => ({
													fontSize: theme.typography.dpDefault?.fontSize,
												})}
											>
												{'(Hay '}
												<Typography
													component="span"
													sx={(theme) => ({
														fontSize: theme.typography.dpDefault?.fontSize,
														fontWeight: theme.typography.fontWeightBold,
														display: 'inline-block',
													})}
												>
													{loading ? <Skeleton variant="text" width="22px" /> : products.length}
												</Typography>
												{' productos en tu pedido)'}
											</Typography>
										</Stack>
										<Stack
											spacing={1}
											sx={{
												maxHeight: 250,
												overflowY: 'auto',
												'&::-webkit-scrollbar': {
													width: '4px',
													backgroundColor: '#f0f0f0',
												},
												'&::-webkit-scrollbar-thumb': {
													backgroundColor: '#888',
													borderRadius: '10px',
												},
											}}
										>
											{!loading ? (
												<React.Fragment>
													{productsGroup.map((product, index) => (
														<HeaderMiniCartDropMenuItemV2
															key={product.code}
															partNumber={product.partNumber}
															thumbnail={product.thumbnail}
															name={product.name}
															price={product.price}
															size={product.size}
															href={product.href}
															quantity={product.quantity}
															onRemove={product.onRemove}
															onAdd={product.onAdd}
															onRemoveAll={removeAll}
															isLast={productsGroup.length - 1 === index}
														/>
													))}
												</React.Fragment>
											) : (
												<HeaderMiniCartDropMenuItemSkeleton />
											)}
										</Stack>
										<Divider
											sx={(theme) => ({
												marginBottom: '11px',
												marginLeft: `-${theme.dimensions.dpSpacing.spacing7}`,
												marginRight: `-${theme.dimensions.dpSpacing.spacing7}`,
											})}
										/>
										<Stack direction="row" justifyContent="space-between" alignItems="center">
											<Box>
												<Typography sx={{ fontSize: '17px' }}>{'Total del pedido:'}</Typography>
												<Typography
													sx={{
														fontSize: '20px',
														fontWeight: 'bold',
													}}
												>
													{loading ? (
														<Skeleton variant="text" width="100px" />
													) : (
														formatPrice('es-MX', 'MXN', grandTotal)
													)}
												</Typography>
											</Box>
											<Stack direction="row" spacing={1}>
												<DpButton
													id="mini-cart-view-full-cart-button"
													data-testid="mini-cart-view-full-cart-button"
													variant="unfocused"
													sx={{
														maxWidth: '166px',
														fontSize: '14px',
														height: '46px',
														padding: '13px',
													}}
													onClick={handleClose}
												>
													{'Seguir comprando'}
												</DpButton>
												<DpButton
													id="mini-cart-view-full-cart-button"
													data-testid="mini-cart-view-full-cart-button"
													variant="primary"
													disabled={productsGroup.length === 0 || loading}
													onClick={() => {
														onClose();
														beneficiarySubmissionContext.onOpen();
													}}
													sx={{
														maxWidth: '223px',
														fontSize: '14px',
														height: '46px',
														padding: '13px',
													}}
												>
													{'Enviar a mi distribuidor'}
												</DpButton>
											</Stack>
										</Stack>
									</Stack>
								) : (
									<Stack spacing={1} alignItems="center" sx={headerMiniCartItemSX}>
										<Typography
											variant="body1"
											align="center"
											sx={(theme) => ({
												fontSize: theme.typography.dpSmall?.fontSize,
											})}
										>
											{CartLabels.Empty.t()}
										</Typography>
									</Stack>
								)}
							</Stack>
						</HeaderMiniCartTourStart>
					</ClickAwayListener>
				</Modal>
			)}
		</CartSetupRender>
	);
};
